/**
 * Environment noise classifier.
 *
 * THE PROBLEM THIS REPLACES
 * Five separate filters had accumulated in index.js, added one incident at a
 * time: a reCAPTCHA rejection, an "invalid origin" string, Sentry's own
 * internal error, and two cross-origin frame patterns. Every one was a reaction
 * to a specific report. None addressed the category, so the next in-app browser
 * produced the next unfamiliar error and the next filter.
 *
 * The underlying category is: **URP's code runs inside environments it does not
 * control.** Facebook, Instagram, Gmail and iMessage all embed a WKWebView that
 * injects its own scripts, patches DOM prototypes, blocks storage APIs, and
 * aborts requests when the user swipes away. Browser extensions do the same on
 * desktop. These produce errors that look alarming, cannot be fixed from here,
 * and — critically — do not affect the user.
 *
 * WHAT THIS IS NOT
 * It is not a blanket suppressor. Three rules keep it honest:
 *
 *   1. A pattern must be attributable to a NAMED external cause. "Sounds
 *      third-party" is not sufficient.
 *   2. It must be non-actionable — nothing in this repo could fix it.
 *   3. It must not affect the user. Anything that breaks a page stays visible,
 *      however noisy.
 *
 * Suppressed events are counted, not silently dropped, so a genuine outbreak
 * is still visible in aggregate. And `classifyError()` is exported so a QA
 * suite can assert that URP's own errors are NEVER classified as noise —
 * without that guard, this file would be the perfect place to hide a real bug.
 */

/**
 * Errors originating in code injected by the host app, an extension, or a
 * third-party SDK's own internals. Each entry names its source.
 */
const THIRD_PARTY_PATTERNS = [
  // ── In-app webview and extension instrumentation ─────────────────────────
  // Host apps patch DOM prototypes and probe iframes. Stripe.js and reCAPTCHA
  // both use hidden cross-origin frames, so they are the usual trigger.
  { re: /Blocked a frame with origin .* from accessing a cross-origin frame/i, src: 'webview/extension probing a cross-origin iframe' },
  { re: /SecurityError:.*cross-origin frame/i,                                 src: 'webview/extension probing a cross-origin iframe' },
  { re: /Permission denied to access property/i,                               src: 'cross-origin frame access, Firefox wording' },
  { re: /Converting circular structure to JSON/i,                              src: 'host script stringifying a DOM node (React fiber cycle)' },

  // ── Host-app script injection ────────────────────────────────────────────
  // NATIVE BRIDGE GLOBALS. Every in-app webview exposes a JS<->native bridge
  // under a well-known global, and the host's injected scripts probe for it.
  // When the probe runs before the bridge is attached — or in a context where
  // it never will be — the host's own code throws inside OUR page and Sentry
  // attributes it to us.
  //
  // Matched by BRIDGE NAME in any phrasing, because the wording differs per
  // engine: Safari says "undefined is not an object (evaluating 'x')", Chrome
  // says "Cannot read properties of undefined (reading 'y')", Firefox says
  // "x is undefined". A previous version listed three specific bridge helper
  // names and therefore missed `window.webkit.messageHandlers` — the single
  // most common one, and the exact error reported from the Facebook browser on
  // 2026-09-15. Name the CATEGORY, not the instances (lesson 84).
  { re: /\b(webkit\.messageHandlers|ReactNativeWebView|chrome\.webview|flutter_inappwebview|__REACT_WEB_VIEW_BRIDGE|androidBridge|JSBridge|webviewBridge)\b/i,
    src: 'in-app webview native bridge probe' },
  { re: /(^|\W)(ucapi|_AutofillCallbackHandler|instantSearchSDKJSBridgeClearHighlight)/i, src: 'in-app browser injected bridge' },
  { re: /webkit-masked-url|safari-(web-)?extension:|chrome-extension:|moz-extension:/i,   src: 'browser extension' },
  { re: /vsc_initialize|__firefox__|zaloJSV2|gCrWeb/i,                                    src: 'host app injected script' },

  // ── Navigation aborts. The user left; nothing failed. ────────────────────
  { re: /^AbortError/i,                                             src: 'request aborted by navigation' },
  { re: /The (operation|fetch) was aborted/i,  src: 'request aborted by navigation' },
  { re: /user aborted a request/i,             src: 'request aborted by navigation' },
  { re: /cancelled$/i,                                              src: 'request cancelled by navigation (Safari)' },
  { re: /Load failed$/i,                                            src: 'Safari network abort on navigation' },

  // ── Storage blocked. Private mode and partitioned webview storage. ───────
  { re: /QuotaExceededError.*(localStorage|setItem)/i, src: 'storage blocked (private mode / partitioned webview)' },
  { re: /The operation is insecure/i,                   src: 'storage blocked by privacy settings' },
  { re: /Access is denied for this document/i,          src: 'storage blocked by privacy settings' },

  // ── Third-party SDK internals ────────────────────────────────────────────
  { re: /Non-recoverable error\.?\s*Do not retry/i,                     src: 'reCAPTCHA in a privacy browser' },
  { re: /^\s*(?:error:\s*)?invalid origin\.?\s*$/i,                     src: 'reCAPTCHA origin check in a webview' },
  { re: /An event processor returned .?null.?, will not send event/i,   src: 'Sentry reporting its own filtering' },
  { re: /Failed to load Stripe\.js/i,                                   src: 'Stripe blocked by an ad blocker or webview' },

  // ── Benign browser quirks ────────────────────────────────────────────────
  // Fires when a scroll handler outlives a frame. Every browser emits it; it
  // has never corresponded to a real defect.
  { re: /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i, src: 'benign browser layout quirk' }
];

/** Script origins that are not ours. An error from here cannot be fixed here. */
const FOREIGN_URL_PATTERNS = [
  /extension:\/\//i,
  /^safari-web-extension:/i,
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /gstatic\.com\/recaptcha/i,
  /connect\.facebook\.net/i
];

/**
 * Classify an error message.
 *
 * @param {string} message
 * @param {string} [culprit] - the script URL Sentry attributes it to
 * @returns {{noise: boolean, source: string|null}}
 */
export function classifyError(message, culprit) {
  const msg = String(message || '');
  if (!msg) return { noise: false, source: null };

  for (const p of THIRD_PARTY_PATTERNS) {
    if (p.re.test(msg)) return { noise: true, source: p.src };
  }

  const url = String(culprit || '');
  if (url) {
    for (const re of FOREIGN_URL_PATTERNS) {
      if (re.test(url)) return { noise: true, source: `third-party script: ${url.slice(0, 80)}` };
    }
  }

  return { noise: false, source: null };
}

/** Suppression counts, so an outbreak is visible even though events are dropped. */
const counts = Object.create(null);

export function recordSuppression(source) {
  counts[source] = (counts[source] || 0) + 1;
}

export function suppressionCounts() {
  return { ...counts };
}

export default classifyError;
